"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useWhatsappSignupConfigQuery } from "@/hooks/queries/whatsapp-signup-config"
import { useConnectWhatsappMutation } from "@/hooks/mutations/whatsapp-connect"
import {
  WHATSAPP_COEXISTENCE_FEATURE_TYPE,
  isTrustedEmbeddedSignupOrigin,
  parseEmbeddedSignupMessage,
  type EmbeddedSignupAssets,
} from "@/lib/agents/messaging/channels/whatsapp/embedded-signup"

// Minimal FB JS SDK surface we use. The SDK attaches itself to window.
declare global {
  interface Window {
    FB?: {
      init: (params: {
        appId: string
        autoLogAppEvents?: boolean
        xfbml?: boolean
        version: string
      }) => void
      login: (
        cb: (response: { authResponse?: { code?: string } | null; status?: string }) => void,
        opts: Record<string, unknown>
      ) => void
    }
    fbAsyncInit?: () => void
  }
}

const FB_SDK_ID = "facebook-jssdk"
const FB_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js"

interface UseEmbeddedSignupOptions {
  agentId: string
  onConnected: () => void
  onError?: (message: string) => void
}

export interface UseEmbeddedSignupResult {
  /** Launch the Embedded Signup popup. No-op until `ready`. */
  launch: (reauthorize?: boolean) => void
  /** SDK loaded + config present — the connect affordance can be enabled. */
  ready: boolean
  /** The server exchange is in flight. */
  connecting: boolean
  /** appId + config id are set in system settings. */
  configured: boolean
  /** True once the config query has resolved (avoids a "not configured" flash). */
  configLoaded: boolean
}

/**
 * Drives the WhatsApp Embedded Signup client flow: loads the FB SDK, listens for
 * the completion message (waba_id + phone_number_id), runs FB.login to get the
 * exchangeable code, then hands all three to the connect server action. The
 * caller renders the trigger button and reflects `ready`/`connecting`.
 */
export function useWhatsappEmbeddedSignup({
  agentId,
  onConnected,
  onError,
}: UseEmbeddedSignupOptions): UseEmbeddedSignupResult {
  const { data: config, isSuccess: configLoaded } = useWhatsappSignupConfigQuery()
  const connect = useConnectWhatsappMutation()
  const [sdkReady, setSdkReady] = useState(false)
  // The FINISH message (ids) arrives separately from the login callback (code);
  // stash it in a ref so the callback can read it.
  const assets = useRef<EmbeddedSignupAssets | null>(null)

  // Load + init the FB SDK once the public config is available.
  useEffect(() => {
    if (!config?.configured) return
    if (window.FB) {
      setSdkReady(true)
      return
    }
    window.fbAsyncInit = () => {
      window.FB?.init({
        appId: config.appId,
        autoLogAppEvents: true,
        xfbml: false,
        version: config.graphVersion,
      })
      setSdkReady(true)
    }
    if (!document.getElementById(FB_SDK_ID)) {
      const script = document.createElement("script")
      script.id = FB_SDK_ID
      script.src = FB_SDK_SRC
      script.async = true
      script.crossOrigin = "anonymous"
      document.body.appendChild(script)
    }
  }, [config])

  // Capture the Embedded Signup completion message (phone_number_id + waba_id).
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (!isTrustedEmbeddedSignupOrigin(event.origin)) return
      const parsed = parseEmbeddedSignupMessage(event.data)
      if (parsed) assets.current = parsed
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [])

  const launch = useCallback((reauthorize = false) => {
    assets.current = null
    if (!window.FB || !config?.configured) return
    window.FB.login(
      // Must be a plain function — the FB SDK type-asserts the callback and
      // throws "Expression is of type asyncfunction, not function" on async.
      (response) => {
        const code = response?.authResponse?.code
        const captured = assets.current
        if (!code || !captured) {
          onError?.("WhatsApp signup was cancelled or didn't complete.")
          return
        }
        connect
          .mutateAsync({ agentId, code, ...captured, ...(reauthorize ? { reauthorize: true } : {}) })
          .then(onConnected)
          .catch((e) => {
            onError?.(e instanceof Error ? e.message : "Failed to connect WhatsApp.")
          })
      },
      {
        config_id: config.configId,
        response_type: "code",
        override_default_response_type: true,
        // featureType replaces the WABA-selection screen with one that also
        // offers connecting an existing WhatsApp Business app account, so one
        // tile still serves both intents.
        extras: { setup: {}, featureType: WHATSAPP_COEXISTENCE_FEATURE_TYPE },
      }
    )
  }, [config, connect, agentId, onConnected, onError])

  return {
    launch,
    ready: sdkReady && Boolean(config?.configured),
    connecting: connect.isPending,
    configured: Boolean(config?.configured),
    configLoaded,
  }
}
